import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import PageShell from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import {
  Target, LineChart, Sparkles, CalendarDays, Bell, Trophy,
  Users, ShieldCheck, MessagesSquare, Check,
} from "lucide-react";

const FEATURES = [
  { icon: Target, title: "AI audience targeting", desc: "We match your niche, interests and location to the audiences most likely to follow and engage — no bots, no fake numbers." },
  { icon: LineChart, title: "Real, live analytics", desc: "Track real self-logged follower counts alongside projected growth so you always know exactly where you stand." },
  { icon: Sparkles, title: "AI Content Studio", desc: "Generate scroll-stopping hooks, scripts, captions, bios and comment replies tuned to each platform in seconds." },
  { icon: CalendarDays, title: "Smart scheduling calendar", desc: "Plan your week, auto-fill ideas, and post at your best-performing times with timezone-aware reminders." },
  { icon: Bell, title: "Reminders & recaps", desc: "Daily posting nudges and a weekly growth recap delivered to your inbox keep your momentum on track." },
  { icon: Trophy, title: "Milestones & streaks", desc: "Celebrate follower milestones, build posting streaks, earn rewards, and use a streak freeze when life happens." },
  { icon: Users, title: "Multi-platform growth", desc: "Grow TikTok, Instagram, YouTube and Facebook side by side, each with its own handle and tracking." },
  { icon: MessagesSquare, title: "Live support built in", desc: "AI-assisted chat and a full ticket system with email reply-in — help is always one tap away." },
  { icon: ShieldCheck, title: "Safe & password-free", desc: "We never ask for your social passwords. Growth is 100% organic and account-safe." },
];

const Features = () => {
  const navigate = useNavigate();
  return (
    <PageShell
      testid="features-page"
      kicker="Features"
      title="Everything you need to grow"
      subtitle="CloutRise pairs AI content tools with real analytics and a growth engine built for the algorithm — all in one place."
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {FEATURES.map((f, i) => (
          <motion.div key={f.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.06 }}
            className="rounded-2xl border border-border/50 bg-card p-7 hover:border-primary/30 transition-colors" data-testid={`features-item-${i}`}>
            <div className="w-11 h-11 rounded-lg bg-secondary/10 border border-secondary/20 flex items-center justify-center mb-5">
              <f.icon className="w-5 h-5 text-secondary" />
            </div>
            <h3 className="font-heading text-xl font-bold mb-2">{f.title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
          </motion.div>
        ))}
      </div>

      <div className="mt-14 rounded-2xl border border-primary/30 bg-primary/5 p-8 md:p-10 text-center">
        <h2 className="font-heading text-3xl font-black tracking-tight">Ready to start growing?</h2>
        <p className="mt-3 text-muted-foreground max-w-xl mx-auto">Launch your campaign in minutes. Cancel anytime, with a 7-day money-back guarantee on every plan.</p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Button onClick={() => navigate("/register")} className="rounded-full bg-primary text-background font-bold hover:bg-primary/90 hover:scale-105 transition-transform" data-testid="features-cta-register">
            Start growing free
          </Button>
          <Button variant="outline" onClick={() => navigate("/plans")} className="rounded-full border-white/15" data-testid="features-cta-plans">
            View plans
          </Button>
        </div>
        <ul className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
          {["No bots", "No passwords", "Cancel anytime"].map((t) => (
            <li key={t} className="flex items-center gap-1.5"><Check className="w-4 h-4 text-primary" /> {t}</li>
          ))}
        </ul>
      </div>
    </PageShell>
  );
};

export default Features;
